import { useEffect, useState } from "react";
import { Button, Input, LoadingCard, showToast } from "@xpense/ui";
import { api, Transaction, PaginatedResponse } from "../api/client";
import { EmptyState, Page, RetryButton, Section } from "../components/Page";
import { Icon } from "../components/Icons";
import TransactionCard from "../components/TransactionCard";
import EditTransactionModal from "../components/EditTransactionModal";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";

export default function Transactions() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [manualText, setManualText] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingTxn, setEditingTxn] = useState<Transaction | null>(null);
  const [deletingTxn, setDeletingTxn] = useState<Transaction | null>(null);

  useEffect(() => {
    loadTransactions();
  }, [page]);

  const loadTransactions = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get<PaginatedResponse<Transaction>>(
        `/api/v1/transactions?page=${page}&page_size=20`
      );
      setTransactions(response.data);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!manualText.trim()) return;
    setAdding(true);
    try {
      await api.post("/api/v1/events/ingest", {
        source: "manual",
        text: manualText.trim(),
      });
      setManualText("");
      showToast("Transaction queued for processing", "success");
      loadTransactions();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to add transaction", "error");
    } finally {
      setAdding(false);
    }
  };

  const handleSave = (updated: Transaction) => {
    setTransactions((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
    setEditingTxn(null);
  };

  const handleDeleted = (id: string) => {
    setTransactions((prev) => prev.filter((t) => t.id !== id));
    setTotal((prev) => prev - 1);
    setDeletingTxn(null);
  };

  const totalPages = Math.max(1, Math.ceil(total / 20));

  if (loading && transactions.length === 0) {
    return (
      <Page
        eyebrow="Ledger"
        title="Transactions"
        description="Searchable, reviewable money movement from every ingested source."
      >
        <LoadingCard />
        <LoadingCard />
        <LoadingCard />
      </Page>
    );
  }

  if (error) {
    return (
      <Page eyebrow="Ledger" title="Transactions">
        <Section>
          <EmptyState
            icon="warning"
            title="Failed to load transactions"
            description={error}
            action={<RetryButton onClick={loadTransactions} />}
            tone="danger"
          />
        </Section>
      </Page>
    );
  }

  return (
    <Page
      eyebrow="Ledger"
      title="Transactions"
      description="Add a manual event or inspect the complete AI-classified transaction history."
      action={
        <Button variant="secondary" size="sm" onClick={loadTransactions}>
          <Icon name="refresh" />
          Refresh
        </Button>
      }
    >
      <Section
        title="Quick add"
        description="Paste a natural-language transaction. The classifier will handle structure and confidence."
      >
        <div className="xpn-section-body">
          <div className="xpn-flex xpn-gap-md xpn-items-start" style={{ flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 320px" }}>
              <Input
                aria-label="Transaction description"
                value={manualText}
                onChange={(e) => setManualText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAdd();
                }}
                placeholder="Paid rent 8800 via UPI"
                disabled={adding}
              />
            </div>
            <Button onClick={handleAdd} loading={adding} disabled={adding || !manualText.trim()}>
              <Icon name="plus" />
              Add Transaction
            </Button>
          </div>
        </div>
      </Section>

      <Section
        title="All transactions"
        description={`${total} total records across your account.`}
      >
        {transactions.length === 0 ? (
          <EmptyState
            icon="transactions"
            title="No transactions found"
            description="Add one above or submit an SMS/notification from the mobile app."
          />
        ) : (
          <>
            <div className="xpn-row-list">
              {transactions.map((txn) => (
                <TransactionCard
                  key={txn.id}
                  transaction={txn}
                  onEdit={setEditingTxn}
                  onDelete={setDeletingTxn}
                />
              ))}
            </div>

            <div
              className="xpn-flex-between"
              style={{
                padding: "var(--xpn-space-lg) var(--xpn-space-xl)",
                borderTop: "1px solid var(--xpn-border)",
                flexWrap: "wrap",
                gap: "var(--xpn-space-md)",
              }}
            >
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <Icon name="arrowLeft" />
                Previous
              </Button>
              <span className="xpn-text-body-md xpn-text-muted">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
                <Icon name="arrowRight" />
              </Button>
            </div>
          </>
        )}
      </Section>

      {editingTxn && (
        <EditTransactionModal
          transaction={editingTxn}
          open={!!editingTxn}
          onClose={() => setEditingTxn(null)}
          onSaved={handleSave}
        />
      )}

      {deletingTxn && (
        <ConfirmDeleteModal
          transaction={deletingTxn}
          open={!!deletingTxn}
          onClose={() => setDeletingTxn(null)}
          onDeleted={handleDeleted}
        />
      )}
    </Page>
  );
}
